// Operations are replayed whenever their base changes. They must therefore describe
// an absolute, replay-safe change rather than a relative change such as "increment".
export type ApplyOptimisticEntityOperation<TEntity, TOperation> = (
  entity: TEntity | undefined,
  operation: TOperation,
) => TEntity | undefined;

// Combines a mutation response with a base that changed while persistence was in flight.
// Implementations should take response values for fields touched by the operation and
// preserve newer base values for unrelated fields.
export type ReconcileOptimisticEntityMutationResult<TEntity, TOperation> = (
  baseEntity: TEntity | undefined,
  mutationEntity: TEntity | undefined,
  operation: TOperation,
) => TEntity | undefined;

export type OptimisticEntityMutationResult<TEntity> =
  | {
      status: 'success';
      // Omit when the endpoint only acknowledges the operation without returning an entity.
      // Explicit undefined still represents a deleted entity.
      baseEntity?: TEntity | undefined;
    }
  | {
      status: 'failure';
    };

export interface BeginOptimisticMutationResult<TEntity> {
  mutationId: number;
  entity: TEntity | undefined;
}

export type FinishOptimisticMutationResult<TEntity> =
  | {
      status: 'applied';
      entity: TEntity | undefined;
    }
  | {
      status: 'ignored';
    };

interface OptimisticEntityMutation<TOperation> {
  id: number;
  operation: TOperation;
  persistenceBaseRevision: number;
}

interface OptimisticEntityState<TEntity, TOperation> {
  baseEntity: TEntity | undefined;
  baseRevision: number;
  mutations: OptimisticEntityMutation<TOperation>[];
}

interface OptimisticEntityUpdatesOptions<TEntity, TOperation> {
  applyOperation: ApplyOptimisticEntityOperation<TEntity, TOperation>;
  reconcileMutationResult: ReconcileOptimisticEntityMutationResult<TEntity, TOperation>;
}

export class OptimisticEntityUpdates<TEntity, TOperation> {
  #applyOperation: ApplyOptimisticEntityOperation<TEntity, TOperation>;
  #reconcileMutationResult: ReconcileOptimisticEntityMutationResult<TEntity, TOperation>;
  #nextMutationId = 1;
  #latestBaseRevision = 0;
  // Retain base revisions until clear(): an older detail or list request may still be in flight
  // after a newer snapshot completes, including for entities that were deleted.
  // Visible state, persistence, and completion follow mutation order.
  #stateByEntityId = new Map<string, OptimisticEntityState<TEntity, TOperation>>();

  constructor(options: OptimisticEntityUpdatesOptions<TEntity, TOperation>) {
    this.#applyOperation = options.applyOperation;
    this.#reconcileMutationResult = options.reconcileMutationResult;
  }

  beginMutation(
    entityId: string,
    currentEntity: TEntity,
    operation: TOperation,
  ): BeginOptimisticMutationResult<TEntity> {
    const state = this.#stateByEntityId.get(entityId) ?? {
      baseEntity: currentEntity,
      baseRevision: this.#latestBaseRevision,
      mutations: [],
    };
    const mutation = {
      id: this.#nextMutationId,
      operation,
      persistenceBaseRevision: state.baseRevision,
    };

    this.#nextMutationId += 1;
    const nextState = {
      ...state,
      mutations: [...state.mutations, mutation],
    };

    this.#stateByEntityId.set(entityId, nextState);

    return {
      mutationId: mutation.id,
      entity: this.#projectEntity(nextState),
    };
  }

  getEntity(entityId: string): TEntity | undefined {
    return this.#getOptimisticEntity(entityId);
  }

  captureBaseRevision(): number {
    return this.#latestBaseRevision;
  }

  startPersistence(entityId: string, mutationId: number): void {
    const state = this.#stateByEntityId.get(entityId);

    if (state == null) {
      return;
    }

    const mutation = state.mutations.find((candidateMutation) => {
      return candidateMutation.id === mutationId;
    });

    if (mutation == null) {
      return;
    }

    this.#assertMutationIsNext(entityId, mutationId, state);

    const mutations = state.mutations.map((mutation) => {
      if (mutation.id !== mutationId) {
        return mutation;
      }

      return {
        ...mutation,
        persistenceBaseRevision: state.baseRevision,
      };
    });

    this.#stateByEntityId.set(entityId, { ...state, mutations });
  }

  reconcileBaseEntity(entityId: string, entity: TEntity | undefined): TEntity | undefined {
    const baseRevision = this.#advanceBaseRevision();
    const mutations = this.#stateByEntityId.get(entityId)?.mutations ?? [];

    const nextState = {
      baseEntity: entity,
      baseRevision,
      mutations,
    };

    this.#stateByEntityId.set(entityId, nextState);

    return this.#projectEntity(nextState);
  }

  reconcileBaseEntitySnapshot(
    baseRevision: number,
    entityId: string,
    entity: TEntity | undefined,
  ): TEntity | undefined {
    const state = this.#stateByEntityId.get(entityId);

    if (state != null && state.baseRevision > baseRevision) {
      return this.#projectEntity(state);
    }

    return this.reconcileBaseEntity(entityId, entity);
  }

  reconcileBaseSnapshot(
    baseRevision: number,
    byId: Record<string, TEntity>,
  ): Record<string, TEntity> {
    const nextBaseRevision = this.#advanceBaseRevision();
    const nextById = { ...byId };

    for (const [entityId, state] of this.#stateByEntityId) {
      const snapshotIsStale = state.baseRevision > baseRevision;
      const nextState = snapshotIsStale
        ? state
        : {
            ...state,
            baseEntity: byId[entityId],
            baseRevision: nextBaseRevision,
          };
      const optimisticEntity = this.#projectEntity(nextState);

      this.#stateByEntityId.set(entityId, nextState);

      if (optimisticEntity == null) {
        delete nextById[entityId];
        continue;
      }

      nextById[entityId] = optimisticEntity;
    }

    return nextById;
  }

  finishMutation(
    entityId: string,
    mutationId: number,
    result: OptimisticEntityMutationResult<TEntity>,
  ): FinishOptimisticMutationResult<TEntity> {
    const state = this.#stateByEntityId.get(entityId);

    if (state == null) {
      return { status: 'ignored' };
    }

    const mutation = state.mutations.find((candidateMutation) => {
      return candidateMutation.id === mutationId;
    });

    if (mutation == null) {
      return { status: 'ignored' };
    }

    this.#assertMutationIsNext(entityId, mutationId, state);

    const remainingMutations = state.mutations.filter((candidateMutation) => {
      return candidateMutation.id !== mutationId;
    });
    let baseEntity = state.baseEntity;
    let baseRevision = state.baseRevision;

    if (result.status === 'success') {
      const baseChangedDuringPersistence = mutation.persistenceBaseRevision !== state.baseRevision;

      if ('baseEntity' in result) {
        baseEntity = baseChangedDuringPersistence
          ? this.#reconcileMutationResult(state.baseEntity, result.baseEntity, mutation.operation)
          : result.baseEntity;
      } else {
        baseEntity = this.#applyOperation(state.baseEntity, mutation.operation);
      }
      baseRevision = this.#advanceBaseRevision();
    }

    const nextState = {
      baseEntity,
      baseRevision,
      mutations: remainingMutations,
    };
    const entity = this.#projectEntity(nextState);

    this.#stateByEntityId.set(entityId, nextState);

    return {
      status: 'applied',
      entity,
    };
  }

  clear(): void {
    this.#stateByEntityId.clear();
    this.#advanceBaseRevision();
  }

  #getOptimisticEntity(entityId: string): TEntity | undefined {
    const state = this.#stateByEntityId.get(entityId);

    if (state == null) {
      return undefined;
    }

    return this.#projectEntity(state);
  }

  #assertMutationIsNext(
    entityId: string,
    mutationId: number,
    state: OptimisticEntityState<TEntity, TOperation>,
  ): void {
    const nextMutation = state.mutations[0];

    if (nextMutation?.id !== mutationId) {
      throw new Error(
        `Mutation ${mutationId} for entity ${entityId} was processed out of order; expected mutation ${nextMutation?.id}`,
      );
    }
  }

  #advanceBaseRevision(): number {
    this.#latestBaseRevision += 1;

    return this.#latestBaseRevision;
  }

  #projectEntity(state: OptimisticEntityState<TEntity, TOperation>): TEntity | undefined {
    let entity = state.baseEntity;

    // Rebase every pending operation over the latest base entity.
    for (const mutation of state.mutations) {
      entity = this.#applyOperation(entity, mutation.operation);
    }

    return entity;
  }
}
